import React from "react"
import {
  Navigate,
  RouteObject,
  generatePath,
  useLocation,
  useParams,
  Outlet,
  redirect,
  matchPath,
} from "react-router"
import routeNames from "src/router/route-names"
import SponsorsRedirect from "src/pages/sponsors-redirect"
import { createEproModuleRoutes } from "src/pages/epro-module/create-epro-module-routes"
import RootErrorBoundary from "src/root-error-boundary"
import { createLibraryManagerRoutes } from "src/pages/library-manager/create-library-manager-routes"
import { createStudyRoutes } from "src/pages/study/create-study-routes"
import { eproModuleRouteNames } from "src/pages/epro-module/epro-module.route-names"
import { DO_NOT_TRANSLATE_CONSTANTS } from "@myvp/shared/src/constants"
import SponsorsDevTools from "src/components/sponsors-dev-tools"

/**
 * Mutates or adds html elements to the html document's head element.
 */
const DocumentHead = (): null => {
  // set document title
  const location = useLocation()
  React.useEffect(() => {
    // Specifically always English
    if (location.pathname.includes("/studio/")) {
      document.title = `${DO_NOT_TRANSLATE_CONSTANTS.veevaecoa} Studio`
    } else if (location.pathname.includes("/library")) {
      document.title = `${DO_NOT_TRANSLATE_CONSTANTS.veevaecoa} Library Manager`
    } else if (
      matchPath(eproModuleRouteNames.studyPreview.route, location.pathname)
    ) {
      document.title = `${DO_NOT_TRANSLATE_CONSTANTS.veevaecoa} Study Preview`
    } else if (location.pathname.includes("/studies/")) {
      document.title = `${DO_NOT_TRANSLATE_CONSTANTS.veevaecoa} Study Home`
    } else if (location.pathname.startsWith(routeNames.veevaLibrary())) {
      document.title = `${DO_NOT_TRANSLATE_CONSTANTS.veeva} Survey Library`
    }
  }, [location.pathname])

  // add meta tag to prevent search indexing
  React.useEffect(() => {
    if (document.head.querySelector('meta[name="robots"]')) return
    const meta = document.createElement("meta")
    meta.name = "robots"
    meta.content = "noindex"
    document.head.appendChild(meta)
  }, [])

  return null
}

export const Redirect = (props: { to: string }) => {
  const { search } = useLocation()
  const params = useParams()
  const pathname = generatePath(props.to, params)

  return <Navigate replace to={{ pathname, search }} />
}

export const createRedirect = (
  args: { from: string; to: string } | { index: true; to: string }
): RouteObject => ({
  path: "from" in args ? args.from : undefined,
  index: "index" in args ? args.index : undefined,
  element: <Redirect to={args.to} />,
})

export const createRoutes = (): RouteObject[] => {
  return [
    {
      shouldRevalidate: ({ nextParams }) => {
        return (
          nextParams.globalSponsorStudyId !==
            sessionStorage.getItem("globalSponsorStudyId") ||
          nextParams.vaultId !== sessionStorage.getItem("vaultId")
        )
      },
      loader: ({ params }) => {
        if (params.vaultId) {
          sessionStorage.setItem("vaultId", params.vaultId)
        }
        if (params.globalSponsorStudyId) {
          /**
           * Pull the globalSponsorStudyId and pass into sessionStorage which will inevitably go into request headers
           *
           * The reason this exists is that the backend auth service looks at this globalSponsorStudyId and ensures that the user
           * making the request has permission to do the request. Since the auth service doesn't look in the body of a graphql request,
           * we have to pass in a header
           */
          sessionStorage.setItem(
            "globalSponsorStudyId",
            params.globalSponsorStudyId
          )
        }

        return {}
      },
      element: (
        <>
          {
            // tree shake this out of release bundles
            process.env.REACT_APP_release !== "true" &&
            (window.location.hostname.includes(".myveevatrials.com") ||
              window.location.hostname === "sponsors-us.local.com") ? (
              <SponsorsDevTools />
            ) : null
          }
          <DocumentHead />
          <Outlet />
        </>
      ),
      errorElement: <RootErrorBoundary />,
      children: [
        ...createStudyRoutes(),
        ...createEproModuleRoutes(),
        ...createLibraryManagerRoutes(),
        {
          path: routeNames.veevaLibrary.route,
          lazy: () => import("src/pages/veeva-library/veeva-library"),
        },
        {
          path: routeNames.veevaLibraryPreview.route,
          lazy: () =>
            import("src/components/preview/library-manager-preview-page"),
        },
        {
          path: routeNames.error.route,
          lazy: () => import("src/pages/error"),
        },
        {
          path: routeNames.version.route,
          lazy: () => import("src/pages/sponsors-version"),
        },
        {
          path: "/",
          loader: () =>
            redirect(process.env.REACT_APP_vault_login_url as string),
        },
        {
          path: routeNames.sponsorsRedirect.route,
          element: <SponsorsRedirect />,
        },
      ],
      HydrateFallback: () => null,
    },
  ]
}
